(() => {
  if (customElements.get('prelaunch-hero')) return;

  class PrelaunchHero extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.slides = [...this.querySelectorAll('[data-ph-slide]')];
      this.buttons = [...this.querySelectorAll('[data-ph-go]')];
      if (!this.slides.length || !this.buttons.length) return;

      this.events = new AbortController();
      this.desktop = matchMedia('(min-width: 760px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.toggle = this.querySelector('[data-ph-toggle]');
      this.status = this.querySelector('[data-ph-status]');
      this.index = 0;
      this.elapsed = 0;
      this.frame = null;
      this.lastTime = null;
      this.visible = true;
      this.userPaused = this.reduced.matches;
      this.focusPaused = false;
      this.editorPaused = false;

      this.buttons.forEach((button, index) => {
        this.listen(button, 'click', () => this.show(index, true));
      });
      this.listen(this.querySelector('[data-ph-pagination]'), 'keydown', (event) => {
        const current = this.buttons.indexOf(event.target.closest('[data-ph-go]'));
        if (current < 0) return;
        const destinations = {
          ArrowRight: (current + 1) % this.slides.length,
          ArrowLeft: (current - 1 + this.slides.length) % this.slides.length,
          Home: 0,
          End: this.slides.length - 1,
        };
        if (!(event.key in destinations)) return;
        event.preventDefault();
        const next = destinations[event.key];
        this.buttons[next].focus();
        this.show(next, true);
      });
      this.listen(this.toggle, 'click', () => {
        const paused = this.userPaused || this.focusPaused;
        this.userPaused = !paused;
        this.focusPaused = false;
        this.syncPlayback();
      });
      this.listen(this, 'focusin', (event) => {
        if (event.target === this.toggle || !event.target.matches(':focus-visible')) return;
        this.focusPaused = true;
        this.syncPlayback();
      });
      this.listen(this, 'focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.syncPlayback();
      });
      this.listen(document, 'visibilitychange', () => this.syncPlayback());
      this.listen(this.desktop, 'change', () => this.selectMedia());
      this.listen(this.reduced, 'change', () => {
        this.userPaused = this.reduced.matches;
        this.syncPlayback();
      });

      this.querySelectorAll('video').forEach((video) => {
        this.listen(video, 'loadedmetadata', () => {
          if (video === this.video) this.paintProgress();
        });
        this.listen(video, 'timeupdate', () => {
          if (video === this.video) this.paintProgress();
        });
        this.listen(video, 'ended', () => {
          if (video === this.video && this.canRun()) this.advance();
        });
        this.listen(video, 'error', () => {
          if (video !== this.video) return;
          // Leave the poster visible and use the image timer if a video fails.
          this.video = null;
          this.elapsed = 0;
          this.syncPlayback();
        });
      });

      this.querySelectorAll('img').forEach((image) => {
        const ready = () => {
          if (image !== this.image) return;
          this.imageReady = true;
          this.syncPlayback();
        };
        this.listen(image, 'load', ready);
        this.listen(image, 'error', ready);
      });

      this.listen(this, 'shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
        if (index < 0) return;
        this.editorPaused = true;
        this.show(index);
      });
      this.listen(this, 'shopify:block:deselect', () => {
        this.editorPaused = false;
        this.syncPlayback();
      });

      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.syncPlayback();
      });
      this.observer.observe(this);
      this.querySelector('[data-ph-controls]').hidden = false;
      this.dataset.enhanced = 'true';
      this.show(0);
    }

    listen(target, event, callback) {
      target.addEventListener(event, callback, { signal: this.events.signal });
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.observer?.disconnect();
      this.stopClock();
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    show(index, announce = false) {
      this.stopClock();
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.index = index;
      this.slides.forEach((slide, number) => {
        const active = number === index;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        this.buttons[number].setAttribute('aria-current', String(active));
        this.buttons[number].style.setProperty('--ph-progress', '0');
      });
      if (announce) this.status.textContent = `Diapozitiv ${index + 1} od ${this.slides.length}`;
      this.selectMedia();
    }

    selectMedia() {
      this.stopClock();
      this.querySelectorAll('video').forEach((video) => video.pause());
      const slide = this.slides[this.index];
      const screen = this.desktop.matches ? 'desktop' : 'mobile';
      const video = slide.querySelector(`video.prelaunch-hero__media--${screen}`);
      this.video = video && !video.error ? video : null;
      this.image = slide.querySelector(`img.prelaunch-hero__media--${screen}`);
      this.imageReady = !this.image || this.image.complete;
      this.elapsed = 0;
      this.duration = Math.max(1, Number(slide.dataset.imageDuration) || 6) * 1000;
      if (this.video) {
        if (!video.getAttribute('src')) {
          video.preload = 'metadata';
          video.src = video.dataset.src;
        } else {
          video.currentTime = 0;
        }
      }
      this.paintProgress();
      this.syncPlayback();
    }

    canRun() {
      return this.isConnected && !this.userPaused && !this.focusPaused &&
        !this.editorPaused && this.visible && this.imageReady && !document.hidden;
    }

    syncPlayback() {
      const paused = this.userPaused || this.focusPaused || this.editorPaused;
      this.toggle.dataset.paused = String(paused);
      this.toggle.setAttribute('aria-label', paused ? 'Nadaljuj predvajanje' : 'Začasno ustavi predvajanje');
      this.toggle.setAttribute('aria-pressed', String(paused));
      if (!this.canRun()) {
        this.stopClock();
        this.video?.pause();
        return;
      }
      const video = this.video;
      if (video?.ended) {
        this.advance();
        return;
      }
      if (video?.paused) {
        video.play().catch((error) => {
          // A breakpoint or slide change can cancel a pending play request.
          if (error.name === 'AbortError' || video !== this.video || !this.canRun()) return;
          this.userPaused = true;
          this.syncPlayback();
        });
      }
      if (this.frame === null) {
        this.lastTime = performance.now();
        this.frame = requestAnimationFrame((time) => this.tick(time));
      }
    }

    stopClock() {
      if (this.frame != null) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.lastTime = null;
    }

    tick(time) {
      this.frame = null;
      if (!this.canRun()) return;
      if (!this.video) this.elapsed += Math.max(0, time - (this.lastTime ?? time));
      this.lastTime = time;
      this.paintProgress();
      if (!this.video && this.elapsed >= this.duration) {
        this.advance();
        return;
      }
      this.frame = requestAnimationFrame((nextTime) => this.tick(nextTime));
    }

    paintProgress() {
      const video = this.video;
      const progress = video
        ? (Number.isFinite(video.duration) && video.duration > 0 ? video.currentTime / video.duration : 0)
        : this.elapsed / this.duration;
      this.buttons[this.index].style.setProperty('--ph-progress', String(Math.min(1, Math.max(0, progress || 0))));
    }

    advance() {
      this.show((this.index + 1) % this.slides.length);
    }
  }

  customElements.define('prelaunch-hero', PrelaunchHero);
})();
